import React, { useState } from 'react';

import useSWR from 'swr';

import { SubscriptionNudgeProps } from './interface';
import {
  BackgroundGrid,
  DiscountedPrice,
  HeaderDescription,
  HeaderTitle,
  Month,
  Offer,
  PaymentContainer,
  PlanBox,
  PlanDetailsContainer,
  PopupContent,
  PremiumButton,
  Price,
  Selectbg,
  Selectbg2,
  SelectedPlanTick,
  SubscriptionContainer,
  Suggested,
  BackgroundWrapper,
  backgroundGridImage,
  selectedPlanStyle
  // TickIcon
} from './SubscriptionNudgeStyle';
import { fetchSubscriptionBannerImages } from '../../../services/apiRequests/SubscriptionBanner/FetchSubscriptionBannerImages';
import { Plan } from '../../../services/responseTypes/displayPlansResponse';
import { colors } from '../../../styles/colors';
import { mixpanelObject } from '../../../utils/analytics';
import { popupCTAEvent } from '../../../utils/analytics/mixpanel/mixpanelEventsHelper';
import { PERCENT_SYMBOL } from '../../../utils/constants/stringConstants';
import {
  calculateDiscountPercentage,
  getBgDimension,
  getCurrencySymbol
} from '../../../utils/SubscriptionHelper/SubscriptionHelper';
import { getBannerImageURL } from '../../../utils/SubscriptionHelper/SubscriptionImageBannerHelper';
import Modal from '../../modal/Modal';
import {
  MIXPANEL_POP_UP_NAME,
  MIXPANEL_POP_UP_NUDGE,
  MIXPANEL_POP_UP_TYPE,
  MIXPANEL_NATIVE,
  MIXPANEL_POP_UP_GROUP,
  MIXPANEL_SUBSCRIPTION,
  MIXPANEL_ELEMENT
} from '../../../utils/analytics/mixpanel/mixpanelConstants';

const SubscriptionNudge = ({
  show,
  onClose,
  currentPlan,
  defaultPlan,
  currentPlanId = '',
  defaultPlanId = '',
  buyPlanFromNudge,
  activePromos,
  newSubscriptionStrings
}: SubscriptionNudgeProps) => {
  // ref
  const [selectedPlanId, setSelectedPlanId] = useState(currentPlanId);

  const planOnClickHandler = (
    event: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    const planId = event.currentTarget.dataset.planId;
    if (planId) setSelectedPlanId(planId);
  };

  const trackMixpanelForNudge = (element: string) => {
    mixpanelObject.setProperty(MIXPANEL_POP_UP_NAME, MIXPANEL_POP_UP_NUDGE);
    mixpanelObject.setProperty(MIXPANEL_POP_UP_TYPE, MIXPANEL_NATIVE);
    mixpanelObject.setProperty(MIXPANEL_POP_UP_GROUP, MIXPANEL_SUBSCRIPTION);
    mixpanelObject.setProperty(MIXPANEL_ELEMENT, element);
    popupCTAEvent();
  };

  const renderPlans = () => {
    let plans: Plan[] = [];
    if (currentPlan && defaultPlan) plans = [defaultPlan, currentPlan];
    else return null;
    return plans.map((plan: Plan, index) => {
      const offerPercentage = calculateDiscountPercentage(
        plan,
        activePromos
      ).toString();

      return (
        <div
          css={selectedPlanStyle(plan.id === selectedPlanId)}
          key={index}
          data-plan-id={plan.id}
          onClick={planOnClickHandler}>
          {plan.id === defaultPlanId && (
            <Suggested data-test-id={`suggested_${plan.id}`}>
              {newSubscriptionStrings.CMS_PLAN_SUGGESTED_TEXT}
            </Suggested>
          )}
          {offerPercentage !== 'NaN' && (
            <Offer data-test-id={`offer_${plan.id}`}>
              {newSubscriptionStrings.CMS_SAVEPERCENT_TEXT.replace(
                '{{discounted_price}}',
                offerPercentage
              )}
              {PERCENT_SYMBOL}
            </Offer>
          )}
          {plan.id === selectedPlanId && (
            <>
              <SelectedPlanTick data-test-id={`selectedPlanTick_${plan.id}`}>
                {/* Todo once font icon on modal issue is fixed */}
                {/* <TickIcon /> */}
                <img src="/webapp-assets/images/selected.svg" alt="tick" />
              </SelectedPlanTick>
              <Selectbg data-test-id={`selectbg${plan.id}`}>
                <Selectbg2 />
              </Selectbg>
            </>
          )}
          <PlanDetailsContainer
            data-test-id={`planDetailsContainer_${plan.id}`}>
            <div>{plan.title}</div>
            <DiscountedPrice data-test-id={`discountedPrice_${plan.id}`}>
              {plan.display_price
                ? getCurrencySymbol(plan.currency) + (plan.display_price || '0')
                : ''}
            </DiscountedPrice>
            <Price data-test-id={`price_${plan.id}`}>
              {getCurrencySymbol(plan.currency) + plan.sell_price}
            </Price>
            <Month data-test-id={`month_${plan.id}`}>
              {plan.duration && newSubscriptionStrings[plan.duration]}
            </Month>
          </PlanDetailsContainer>
        </div>
      );
    });
  };

  const buyPremiumClickHandler = () => {
    trackMixpanelForNudge(selectedPlanId);
    buyPlanFromNudge(selectedPlanId);
  };

  const { data: bannerImages } = useSWR(
    'banner-images',
    fetchSubscriptionBannerImages
  );

  return (
    defaultPlan && (
      <Modal
        show={show}
        onClose={onClose}
        width="416px"
        height="397px"
        background={colors.AppBgColor}
        renderCloseIcon={true}
        closeIconColor={colors.white}
        padding="0"
        handleResponseForSm={true}>
        <PopupContent data-test-id="popupContent">
          {/*
           * TODO: Add placeholder thumbnails, till api is fetching data. i.e. while (!data)
           * useSWR returns error which can be used to remove the loading thumbnails in case API fails.
           *
           * currently handling, if API is successful but bannerImages is {} or [], returning null to dismiss component.
           */}

          {bannerImages &&
          Array.isArray(bannerImages) &&
          bannerImages.length > 0 ? (
            <BackgroundWrapper data-test-id="backgroundWrapper">
              {bannerImages.slice(0, 4).map((bannerImage) => {
                return (
                  <BackgroundGrid
                    data-test-id="backgroundGrid"
                    key={bannerImage.id}>
                    <img
                      css={backgroundGridImage}
                      src={getBannerImageURL(480, bannerImage)}
                      alt={bannerImage.title}
                      title={bannerImage.title}
                      height={getBgDimension('height', 480)}
                      width={getBgDimension('width', 480)}
                    />
                  </BackgroundGrid>
                );
              })}
            </BackgroundWrapper>
          ) : null}

          <SubscriptionContainer data-test-id="subscriptionContainer">
            <HeaderTitle data-test-id="headerTitle">{`${
              selectedPlanId === defaultPlanId
                ? newSubscriptionStrings.GOOD_CHOICE_TEXT
                : newSubscriptionStrings.CMS_SAVEPERCENT_TEXT.replace(
                    '{{discounted_price}}',
                    calculateDiscountPercentage(
                      defaultPlan,
                      activePromos
                    ).toString()
                  ) + `${PERCENT_SYMBOL}`
            }
            `}</HeaderTitle>
            <HeaderDescription data-test-id="headerDescription">
              {selectedPlanId === defaultPlanId
                ? newSubscriptionStrings.NUDGE_SAVE_DESCRIPTION?.replace(
                    '{{discount}}',
                    calculateDiscountPercentage(
                      defaultPlan,
                      activePromos
                    ).toString()
                  )
                : newSubscriptionStrings.NUDGE_PRICE_DESCRIPTION.replace(
                    '{{sell_price}}',
                    defaultPlan?.sell_price.toString() || ''
                  )}
            </HeaderDescription>
            <PlanBox data-test-id="planBox">{renderPlans()}</PlanBox>
          </SubscriptionContainer>
          <PaymentContainer data-test-id="paymentContainer">
            <PremiumButton
              data-test-id="premiumButton"
              onClick={buyPremiumClickHandler}>
              {`${newSubscriptionStrings.CONTINUE_WITH} ${
                selectedPlanId === defaultPlanId
                  ? defaultPlan?.sell_price
                  : currentPlan?.sell_price
              }`}
            </PremiumButton>
          </PaymentContainer>
        </PopupContent>
      </Modal>
    )
  );
};

export default SubscriptionNudge;
